/**
 * On-screen signature capture for test-drive agreements.
 *
 * Pointer events cover mouse, pen and touch alike, so the same pad works on
 * the desk PC and on a tablet handed to the customer. The ink is always drawn
 * black on white — the PNG is a legal record, not a themed UI element, and
 * must stay legible when printed or viewed in dark mode.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

const SignaturePad = forwardRef(function SignaturePad({ height = 180, disabled = false, onChange }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);
  // Held in a ref so an inline callback from the parent does not re-create
  // resize() on every render — which would wipe the signature mid-stroke.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Size the backing store to the element × devicePixelRatio so strokes stay
  // crisp on high-DPI tablets.
  const resize = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    c.width = Math.round(w * dpr);
    c.height = Math.round(height * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, height);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111111';
    setEmpty(true);
    onChangeRef.current?.(true);
  }, [height]);

  useEffect(() => {
    resize();
    // Clearing on resize is deliberate: a scaled signature is not the one the
    // customer drew.
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  const point = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const down = (e) => {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    last.current = point(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2);
    ctx.fillStyle = '#111111';
    ctx.fill();
  };
  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const p = point(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) { setEmpty(false); onChangeRef.current?.(false); }
  };
  const up = () => { drawing.current = false; };

  useImperativeHandle(ref, () => ({
    clear: resize,
    isEmpty: () => empty,
    /** Resolves to a PNG Blob, or null when nothing was drawn. */
    toBlob: () => new Promise((resolve) => {
      if (empty || !canvasRef.current) { resolve(null); return; }
      canvasRef.current.toBlob((b) => resolve(b), 'image/png');
    }),
  }), [empty, resize]);

  return (
    <div style={{ position: 'relative' }}>
      <canvas
        ref={canvasRef}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
        aria-label="Signature pad — sign with mouse, pen or finger"
        style={{
          width: '100%', height, display: 'block', touchAction: 'none',
          background: '#fff', borderRadius: 8,
          border: '1px dashed var(--border-color)',
          cursor: disabled ? 'not-allowed' : 'crosshair',
        }}
      />
      {empty && (
        <div style={{
          position: 'absolute', left: 16, right: 16, bottom: 28,
          borderTop: '1px solid #bbb', pointerEvents: 'none',
        }}>
          <span style={{ fontSize: 11, color: '#888' }}>Sign above this line</span>
        </div>
      )}
      <button type="button" className="btn small" disabled={disabled || empty}
              onClick={resize}
              style={{ position: 'absolute', top: 8, right: 8 }}>
        Clear
      </button>
    </div>
  );
});

export default SignaturePad;
